import type { CookieConsentConfig, Translation } from 'vanilla-cookieconsent';

/**
 * GDPR-compliant cookie consent configuration
 *
 * Categories:
 * - necessary: Essential cookies (always enabled, cannot be disabled)
 * - analytics: Google Analytics and usage tracking
 * - marketing: Google Ads cookies (conversion/remarketing)
 */
export const cookieConsentConfig: CookieConsentConfig = {
  // Cookie persistence settings
  cookie: {
    name: 'gw_cookie_consent',
    // Store consent for 365 days
    expiresAfterDays: 365,
  },

  // Respect Do Not Track browser setting
  // If DNT is enabled, default to rejecting non-essential cookies
  guiOptions: {
    consentModal: {
      layout: 'box',
      position: 'bottom right',
      flipButtons: false,
    },
  },

  // Categories of cookies
  categories: {
    necessary: {
      enabled: true,
      readOnly: true, // Cannot be disabled
    },
    analytics: {
      enabled: false,
      readOnly: false,
      // Auto-clear cookies when rejected
      autoClear: {
        cookies: [
          {
            name: /^_ga/, // Google Analytics cookies
          },
          {
            name: '_gid',
          },
        ],
      },
    },
    marketing: {
      enabled: false,
      readOnly: false,
      autoClear: {
        cookies: [
          {
            name: /^_gcl/, // Google Ads cookies
          },
        ],
      },
    },
  },

  // Language configuration
  language: {
    default: 'en',
    translations: {
      en: {
        consentModal: {
          title: 'Cookie Consent',
          description:
            'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies. You can manage your preferences via Cookie Settings below, or reject non-essential cookies.',
          acceptAllBtn: 'Accept All',
          acceptNecessaryBtn: 'Reject All',
          footer: `
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/terms-of-service">Terms of Service</a>
            <a href="/cookie-settings">Cookie Settings</a>
          `,
        },
        // NOTE: preferencesModal is intentionally omitted — the single granular
        // consent surface is the dedicated /cookie-settings page, not an in-widget
        // preferences modal (asserted by cookieConsentConfig.test.ts). The library's
        // Translation type marks preferencesModal as required; runtime treats it as
        // optional (the modal is simply never shown), so we build the object with a
        // consentModal-only shape and assert it as Translation to keep the config
        // typed without materializing an unused, product-prohibited preferences modal.
      } as Translation,
    },
  },
};

/**
 * Update Google Analytics consent mode based on cookie preferences
 */
export const updateGAConsent = (acceptedCategories: string[]) => {
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: acceptedCategories.includes('analytics') ? 'granted' : 'denied',
      ad_storage: acceptedCategories.includes('marketing') ? 'granted' : 'denied',
      ad_user_data: acceptedCategories.includes('marketing') ? 'granted' : 'denied',
      ad_personalization: acceptedCategories.includes('marketing') ? 'granted' : 'denied',
    });
  }
};

/**
 * Check if Do Not Track is enabled
 */
export const isDoNotTrackEnabled = (): boolean => {
  return (
    navigator.doNotTrack === '1' ||
    // @ts-expect-error - msDoNotTrack is IE-specific
    navigator.msDoNotTrack === '1' ||
    // @ts-expect-error - doNotTrack might be on window
    window.doNotTrack === '1'
  );
};
